// Domain registry of the ACS member space (navigation refonte 21/10, spec docs/research/foundry-coaching-type-2026-10-13/
// ARBORESCENCE-ACS.md). ONE source for: the launcher tiles of the home, the LOCAL dock (entries = rubriques of the current
// domain), the ⌘K « Aller à » group and the mapping of every legacy route onto its domain + rubrique. The rubrique lives in
// the URL (?r=); the last rubrique visited per domain is remembered in memory for the session (no browser storage).
import { createElement, useSyncExternalStore, type ReactNode } from "react";
import { BookOpen, CalendarDays, ChartLine, ClipboardList, Dumbbell, History, Info, Newspaper, NotebookPen, ShoppingBasket, Store, Sun, TrendingUp, Utensils, UtensilsCrossed } from "lucide-react";

export type DomainId = "entrainement" | "nutrition" | "suivi" | "studio";
export interface Rubrique { id: string; label: string; icon: ReactNode; short?: string }
export interface Domain { id: DomainId; label: string; icon: ReactNode; rubriques: Rubrique[] }

const ic = (C: typeof Dumbbell) => createElement(C);

/** Launcher order. Labels exact (spec §3). */
export const DOMAINS: Domain[] = [
  { id: "entrainement", label: "Entraînement", icon: ic(Dumbbell), rubriques: [
    { id: "semaine", label: "Ma semaine", icon: ic(CalendarDays) },
    { id: "historique", label: "Historique", icon: ic(History) },
    { id: "exercices", label: "Exercices", icon: ic(BookOpen) },
  ] },
  { id: "nutrition", label: "Nutrition", icon: ic(Utensils), rubriques: [
    { id: "repas", label: "Repas", icon: ic(UtensilsCrossed) },
    { id: "recettes", label: "Recettes", icon: ic(ClipboardList) },
    { id: "courses", label: "Courses", icon: ic(ShoppingBasket) },
    { id: "journal", label: "Journal", icon: ic(NotebookPen) },
  ] },
  { id: "suivi", label: "Suivi", icon: ic(ChartLine), rubriques: [
    { id: "aujourdhui", label: "Aujourd'hui", icon: ic(Sun) },
    { id: "tendances", label: "Tendances", icon: ic(TrendingUp) },
    { id: "bilans", label: "Bilans", icon: ic(ClipboardList) },
  ] },
  { id: "studio", label: "Studio", icon: ic(Store), rubriques: [
    { id: "actus", label: "Actus", icon: ic(Newspaper) },
    { id: "infos", label: "Infos pratiques", short: "Infos", icon: ic(Info) },
  ] },
];
export const domainOf = (id: DomainId) => DOMAINS.find(d => d.id === id)!;

const routeOf = (hash: string) => hash.replace(/^#\/?/, "").split("?")[0] ?? "";
const param = (hash: string, key: string) => hash.match(new RegExp(`[?&]${key}=([\\w-]+)`))?.[1];
const valid = (d: DomainId, r?: string) => (r && domainOf(d).rubriques.some(x => x.id === r) ? r : undefined);

/** Domain + rubrique of a hash; null = outside any domain (home, account, messages, health, auth, onboarding). */
export function locate(hash: string): { domain: DomainId; rub: string } | null {
  const route = routeOf(hash), r = param(hash, "r"), bloc = param(hash, "bloc");
  switch (route) {
    case "entrainement": return { domain: "entrainement", rub: valid("entrainement", r) ?? "semaine" };
    case "reserver": case "programmations": case "seance": return { domain: "entrainement", rub: "semaine" };
    case "reservations": return { domain: "entrainement", rub: param(hash, "vue") === "historique" ? "historique" : "semaine" };
    case "banque": case "programme": case "ressource": case "ressource-lecture": return { domain: "entrainement", rub: "exercices" };
    case "nutrition": return { domain: "nutrition", rub: valid("nutrition", r) ?? "repas" };
    case "suivi":
      if (bloc === "performances" || bloc === "training") return { domain: "entrainement", rub: "historique" };
      return { domain: "suivi", rub: valid("suivi", r) ?? "aujourdhui" };
    case "bilans": case "bilan-initial": return { domain: "suivi", rub: "bilans" };
    case "studio": return { domain: "studio", rub: valid("studio", r) ?? "actus" };
    default: return null;
  }
}

// Last rubrique per domain, for the session (spec §4: returning to a domain reopens its last rubrique).
const memory: Partial<Record<DomainId, string>> = {};
const remember = (hash: string) => { const l = locate(hash); if (l) memory[l.domain] = l.rub; };
if (typeof window !== "undefined") { remember(window.location.hash); window.addEventListener("hashchange", () => remember(window.location.hash)); }

/** Hash of a domain rubrique; without rubrique: the remembered one, else the first. */
export function rubHref(domain: DomainId, rub?: string): string {
  const r = rub ?? memory[domain] ?? domainOf(domain).rubriques[0]!.id;
  return `#/${domain}?r=${r}`;
}

const subscribeHash = (l: () => void) => { window.addEventListener("hashchange", l); return () => window.removeEventListener("hashchange", l); };
const readHash = () => window.location.hash;
/** The current hash, re-rendering on every change (same route, new ?r= included). */
export const useHash = () => useSyncExternalStore(subscribeHash, readHash, readHash);

/** Current rubrique of a hub, read from the URL (defaults through locate). */
export function useRubrique(domain: DomainId): string {
  const l = locate(useHash());
  return l && l.domain === domain ? l.rub : domainOf(domain).rubriques[0]!.id;
}
